import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, RotateCcw, MapPin, Loader2, CheckCircle2, AlertTriangle, Send, X, ChevronLeft } from "lucide-react";
import { mockSubmitAttendance } from "../../controller/mobile/mockapi";
import type { AttendanceType, GeoState, SubmitState, ToastMessage } from "../../controller/mobile/types";

async function convertFileToWebP(file: File, quality = 0.8): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  if (file.type === "image/webp") return file;

  const url = URL.createObjectURL(file);
  const image = new Image();
  image.src = url;

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("Gagal memuat gambar untuk konversi WebP."));
  });

  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    URL.revokeObjectURL(url);
    return file;
  }

  ctx.drawImage(image, 0, 0);
  URL.revokeObjectURL(url);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((result) => resolve(result), "image/webp", quality)
  );

  if (!blob) return file;
  const webpFile = new File([blob], file.name.replace(/\.[^.]+$/, ".webp"), {
    type: "image/webp",
    lastModified: Date.now(),
  });
  return webpFile;
}

interface CapturePageProps {
  type: AttendanceType;
  id_karyawan : string;
  onCancel: () => void;
  onSuccess: () => void;
}
 
export default function CapturePage({
  type,
  id_karyawan,
  onCancel,
  onSuccess,
}: CapturePageProps): React.ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);
 
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [capturedAt, setCapturedAt] = useState<string | null>(null);
  const [now, setNow] = useState<Date>(new Date());
 
  const [geo, setGeo] = useState<GeoState>({ status: "idle", coords: null });
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [stampMounted, setStampMounted] = useState(false);
  const [uploadStage, setUploadStage] = useState<"idle" | "converting" | "uploading">("idle");
 
  const bothReady = photo !== null && geo.status === "success";
  const isSubmitTimeAllowed = (date: Date): boolean => {
    const totalMinutes = date.getHours() * 60 + date.getMinutes();
    const morningStart = 1 * 60;
    const morningEnd = 9 * 60;
    const afternoonStart = 9 * 60;
    const afternoonEnd = 24 * 60;
    // return (
    //   (totalMinutes >= morningStart && totalMinutes <= morningEnd) ||
    //   (totalMinutes >= afternoonStart && totalMinutes <= afternoonEnd)
    // );
    return true;
  };

  const submitAllowed = isSubmitTimeAllowed(now);
  // const submitAllowed = true;
  const canSubmit =
    photo !== null &&
    geo.status === "success" &&
    geo.coords !== null &&
    capturedAt !== null &&
    submitState !== "loading" &&
    submitAllowed;
  const busy = uploadStage !== "idle" || submitState === "loading";
  const isSubmitting = submitState === "loading";
 
  const title = type === "masuk" ? "Absen Masuk" : "Absen Pulang";
 
  useEffect(() => {
    if (bothReady) {
      const t = setTimeout(() => setStampMounted(true), 60);
      return () => clearTimeout(t);
    }
    setStampMounted(false);
  }, [bothReady]);
 
  useEffect(() => { if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(intervalId);
  }, []);
 
  useEffect(() => { return () => { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); }; }, []);
 
  const requestLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGeo({
        status: "error",
        coords: null,
        error: "Geolocation tidak didukung perangkat ini.",
      });
      return;
    }
 
    setGeo((prev) => ({ ...prev, status: "loading", error: undefined }));
 
    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        setGeo({
          status: "success",
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          },
        });
      },
      (err: GeolocationPositionError) => {
        let message = "Gagal mendapatkan lokasi.";
        if (err.code === err.PERMISSION_DENIED) {
          message = "Akses lokasi ditolak. Aktifkan izin lokasi di browser.";
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          message = "Lokasi tidak tersedia saat ini.";
        } else if (err.code === err.TIMEOUT) {
          message = "Waktu permintaan lokasi habis.";
        }
        setGeo({ status: "error", coords: null, error: message });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);
 
  const handleTakePhotoClick = () => fileInputRef.current?.click();
 
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
 
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    const url = URL.createObjectURL(file);
 
    setPhoto(file);
    setPhotoPreviewUrl(url);
    setCapturedAt(new Date().toISOString());
    requestLocation();
 
    e.target.value = "";
  };
 
  const handleRetake = () => {
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhoto(null);
    setPhotoPreviewUrl(null);
    setCapturedAt(null);
    setGeo({ status: "idle", coords: null });
    setErrorMessage(null);
  };
 
  const handleSubmit = async () => {
    if (!photo || geo.status !== "success" || !geo.coords || !capturedAt) return;
    if (!submitAllowed) {
      setToast({
        type: "error",
        message: "Absensi hanya bisa dikirim pada pukul 07.00–09.00 atau 16.00–18.00.",
      });
      return;
    }

    try {
      setErrorMessage(null);
      setUploadStage("converting");
      setToast({ type: "success", message: "Mengonversi gambar ke WebP..." });
      const webpPhoto = await convertFileToWebP(photo, 0.8);

      setUploadStage("uploading");
      setSubmitState("loading");
      setToast({ type: "success", message: "Mengunggah absensi ke server..." });

      const formData = new FormData();
      formData.append("type", type);
      formData.append("photo", webpPhoto);
      formData.append("latitude", String(geo.coords.latitude));
      formData.append("longitude", String(geo.coords.longitude));
      formData.append("timestamp", capturedAt);
      formData.append("id_karyawan", id_karyawan);

      const result = await mockSubmitAttendance(formData);
      if (result.status == 201 || result.status == "201") {
        setSubmitState("success");
        setToast({ type: "success", message: result.message });
        setErrorMessage(null);
      } else if (result.status == 406 || result.status == "406") {
        setSubmitState("error");
        setToast({ type: "error", message: result.message });
        setErrorMessage(String(result.message || "Pengiriman gagal."));
      } 
      else {
        setSubmitState("error");
        setToast({ type: "error", message: result.message });
        setErrorMessage(String(result.message || "Pengiriman gagal."));
      }
    } catch (err) {
      setSubmitState("error");
      const msg = err instanceof Error ? err.message : String(err || "Terjadi kesalahan tak terduga. Coba lagi.");
      setToast({ type: "error", message: msg });
      setErrorMessage(msg);
    } finally {
      setUploadStage("idle");
    }
  };
 
  const handleDone = () => {
    if (!geo.coords || !capturedAt) return;
    onSuccess();
  };
 
  const capturedAtLabel = capturedAt
    ? new Date(capturedAt).toLocaleString("id-ID", {
        dateStyle: "medium",
        timeStyle: "medium",
      })
    : null;

  let footerInner: React.ReactNode;
  if (busy) {
    footerInner = (
      <>
        <Loader2 className="h-4 w-4 animate-spin" />
        {uploadStage === "converting" ? "Mengonversi..." : "Mengunggah..."}
      </>
    );
  } else if (isSubmitting) {
    footerInner = (
      <>
        <Loader2 className="h-4 w-4 animate-spin" />
        Mengirim...
      </>
    );
  } else {
    footerInner = (
      <>
        <Send className="h-4 w-4" />
        Kirim {title}
      </>
    );
  }
 
  return (
    <div className="relative flex h-full flex-col">
      {toast && (
        <div
          className={`absolute inset-x-4 top-4 z-50 flex items-start gap-2 rounded-xl border px-4 py-3 shadow-lg transition-all duration-300 ${
            toast.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-rose-200 bg-rose-50 text-rose-800"
          }`}
          role="status"
        >
          {toast.type === "success" ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <p className="flex-1 text-sm font-medium leading-snug">
            {toast.message}
          </p>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="shrink-0 text-current/60 hover:text-current"
            aria-label="Tutup notifikasi"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
 
      {/* Header */}
      <header className="flex shrink-0 flex-col gap-2 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={
              `rounded-full p-1.5 ${busy ? "text-slate-300 pointer-events-none" : "text-slate-500 hover:bg-slate-100"}`
            }
            aria-label="Kembali"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h1 className="text-base font-semibold text-slate-900">{title}</h1>
          { (uploadStage !== "idle" || isSubmitting) && (
            <p className="ml-3 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {uploadStage === "converting" ? "Mengonversi gambar ke WebP..." : "Mengunggah ke server..."}
            </p>
          )}
        </div>
      </header>
 
      <main className="flex-1 overflow-y-auto px-5 py-5">
        {submitState === "success" ? (
          <div className="flex h-full flex-col items-center justify-center gap-5 py-8 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle2 className="h-9 w-9 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {title} Berhasil
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Kehadiran kamu sudah tercatat di sistem.
              </p>
            </div>
 
            {photoPreviewUrl && (
              <img
                src={photoPreviewUrl}
                alt="Selfie absensi"
                className="h-32 w-32 rounded-2xl object-cover ring-4 ring-emerald-100"
              />
            )}
 
            <div className="w-full space-y-2 rounded-2xl border border-slate-200 bg-white p-4 text-left text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Waktu</span>
                <span className="font-mono text-slate-800">
                  {capturedAtLabel}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Koordinat</span>
                <span className="font-mono text-slate-800">
                  {geo.coords?.latitude.toFixed(6)},{" "}
                  {geo.coords?.longitude.toFixed(6)}
                </span>
              </div>
            </div>
 
            <button
              type="button"
              onClick={handleDone}
              className="w-full rounded-2xl bg-slate-900 py-3.5 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Selesai
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <section>
              <p className="mb-2 text-sm font-medium text-slate-700">
                1. Ambil selfie
              </p>
 
              <div className="relative mx-auto aspect-square w-full max-w-[260px] overflow-hidden rounded-3xl">
                {photoPreviewUrl ? (
                  <>
                    <img
                      src={photoPreviewUrl}
                      alt="Preview selfie"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRetake}
                      disabled={busy}
                      className={
                        `absolute right-3 top-3 flex items-center gap-1 rounded-full bg-black/50 px-3 py-1.5 text-xs font-medium text-white backdrop-blur ${
                          busy ? "opacity-60 pointer-events-none" : "hover:bg-black/60"
                        }`
                      }
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Ambil ulang
                    </button>
 
                    <div
                      className={`pointer-events-none absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full border-2 border-emerald-400 bg-emerald-500/90 px-3 py-1.5 text-white shadow-lg transition-all duration-500 ease-out ${
                        stampMounted
                          ? "-rotate-6 scale-100 opacity-100"
                          : "rotate-0 scale-50 opacity-0"
                      }`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span className="text-[11px] font-bold uppercase tracking-wide">
                        Terverifikasi
                      </span>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleTakePhotoClick}
                    disabled={busy}
                    className={`flex h-full w-full flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-slate-300 bg-white text-slate-400 transition-colors ${
                      busy ? "cursor-not-allowed opacity-60" : "hover:border-slate-400 hover:text-slate-500"
                    }`}
                  >
                    <Camera className="h-9 w-9" />
                    <span className="text-sm font-medium">
                      Ketuk untuk ambil foto
                    </span>
                  </button>
                )}
              </div>
 
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="user"
                onChange={handlePhotoChange}
                className="hidden"
              />
            </section>
 
            {photo && (
              <section className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="mb-2 text-sm font-medium text-slate-700">
                  2. Deteksi lokasi
                </p>
 
                {geo.status === "loading" && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Mencari lokasi...
                  </div>
                )}
 
                {geo.status === "success" && geo.coords && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                    <div>
                      <p className="font-medium text-emerald-700">
                        Lokasi terdeteksi
                      </p>
                      <p className="font-mono text-xs text-slate-500">
                        {geo.coords.latitude.toFixed(6)},{" "}
                        {geo.coords.longitude.toFixed(6)}
                      </p>
                    </div>
                  </div>
                )}
 
                {geo.status === "error" && (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                      <p className="text-rose-600">{geo.error}</p>
                    </div>
                    <button
                      type="button"
                      onClick={requestLocation}
                      className="text-xs font-medium text-slate-500 underline underline-offset-4 hover:text-slate-800"
                    >
                      Coba lagi
                    </button>
                  </div>
                )}
              </section>
            )}
 
            {capturedAtLabel && (
              <p className="text-center text-xs text-slate-400">
                Foto diambil pada{" "}
                <span className="font-mono text-slate-500">
                  {capturedAtLabel}
                </span>
              </p>
            )}

            {!submitAllowed && (
              <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>Absensi hanya bisa dikirim pada pukul 07.00–09.00 atau 16.00–18.00.</p>
              </div>
            )}
 
            {submitState === "error" && (
              <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{errorMessage ?? "Pengiriman gagal. Periksa koneksi lalu coba lagi."}</p>
              </div>
            )}
          </div>
        )}
      </main>
 
      {submitState !== "success" && (
        <footer className="shrink-0 border-t border-slate-200 bg-white px-5 py-4">
          <button
            type="button"
            disabled={!canSubmit || busy}
            onClick={handleSubmit}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 py-3.5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
          >
            {footerInner}
          </button>
        </footer>
      )}
    </div>
  );
}